import {store} from 'bukku/redux/store';
import AppActions from "bukku/redux/App/actions";
import {call, put as sagaPut} from "redux-saga/effects";
import {getAccessTokenAndCompanySubdomain} from "./ApiService/helper/getAccessTokenAndCompanySubdomain";
import {SagaContext} from "./SagaService";
import queryString from 'query-string';
import {t} from "@lingui/macro";
import { InterceptorType } from '../components/Modal/Interceptors';
import dayjs from "dayjs";
import LoggerActions from 'bukku/redux/Logger/actions';
import { MomentFormatEnums } from '../enums/MomentEnums';

const {
    setVersion,
} = AppActions;

const apiUrl = process.env.REACT_APP_PROXY_API_URL;
const mockInterceptor = {
    isDebug: false,
    message: 'The selected parent account is already used. All transactions / settings using the parent account will be changed to the new account that you are creating if you’d like to proceed.',
    intercept_type: InterceptorType.account_used,
    status: 428,
};
/**
 * Handle Json response.
 **/
const handleResponse = async (response, context = new SagaContext()) => {
    // Inject version
    const version = response.headers.get('bukku-version');
    if (version)
        store.dispatch(setVersion(version));

    // console.log('aaa ApiService - handleResponse - 0', {response});

    let data = {};
    if (context.isPdfByte && response.ok) {
        // https://stackoverflow.com/questions/38705385/embed-pdf-byte-data-in-html
        const blob = await response.blob();
        const blobUrl = URL.createObjectURL(blob);
        response.data = {
            blobUrl,
        };
    }
    else {
        data = await response.json();
        response.data = data;
        // console.log('aaa ApiService - handleResponse - 1 (DATA)', { data });
    }

    // response.data = data;
    if (response.ok)
        return response;

    // Throw error
    const e = new Error();

    const {
        isDebug: isDebugInterceptor,
        message: debugMessage,
        intercept_type: debugType,
        status,
    } = mockInterceptor;
    const responseStatus = isDebugInterceptor ? status : response.status;
    switch (responseStatus) {
        case 422:
            // batch import contact, products, if there are errors, show a modal displaying the errors
            if (
                response.url.includes("/contacts/batch") ||
                response.url.includes("/products/batch")
            ) {
                e.errors = data.errors;
                break;
            }

            if (data.errors) {
                e.errors = data.errors;
                Object.keys(data.errors).forEach(error => {
                    // skip loop if the property is from prototype
                    if (!data.errors.hasOwnProperty(error))
                        return null;

                    e.message = data.errors[error].join(" ");
                    return null;
                });
            }
            else if (data.message) {
                e.message = data.message;
            }

            break;
            // while in SessionInactiveModal, logged in with invalid password
        case 400:
            if (data.message)
                e.message = data.message;
            else
                e.message = t`User is not authenticated, please check your login credentials.`;
            break;
        case 401: // Invalid credentials.
            switch (window.location.pathname) {
                case '/signin':
                case '/signup':
                case '/activate':
                    break;
                default: // Redirect to login
                    // store.dispatch(openAppModal(AppModalType.SESSION_EXPIRED_MODAL));
                    // history.push('/signin');
                    // console.log('aaa 401', { context, data, response });
                    break;
            }

            if (data.message)
                e.message = data.message;
            else
                e.message = t`User is not authenticated, please check your login credentials.`;
            break;
        case 403: // Not authorised.
            if (data.message)
                e.message = data.message;
            else
                e.message = t`You are not authorised to access the content.`;
            break;

        case 428:
        case 412:
            if (context.allow_intercept) {
                console.log('aaa APISERVICE', data);
                e.has_intercepted = {
                    message: isDebugInterceptor ? debugMessage : data.message,
                    intercept_type: isDebugInterceptor ? debugType : data.intercept_type,
                    items: data.items,
                    action: context.action,
                    settings: data.settings, // SPECIFIC FOR SETTINGS_CHECK
                    details: {
                        ...data.details,
                        nonSupportedTransactionFees: data.non_supported_transaction_fees,
                        statementNumbers: data.statement_numbers,
                    },
                };
            }
            else {
                e.canSuppress = !!response.headers.get('can-suppress');
                if (data.message)
                    e.message = data.message;
                else
                    e.message = t`Unknown Error ${response.status}`;
            }
            break;
        // case 302: // for maybank feed, when click on 'Connect' button in /bank/feeds
        //     window.location.replace(response.headers.get('location'));
        //     break;
        default: // Unknown, print the message.
            e.canSuppress = !!response.headers.get('can-suppress');
            if (data.message)
                e.message = data.message;
            else
                e.message = t`Unknown Error ${response.status}`;
            break;
    }

    e.statusCode = responseStatus;
    throw e;
};

export function getCompanySubdomain() {
    const { companySubdomain } = getAccessTokenAndCompanySubdomain();
    return companySubdomain;
}

async function request(method, path, providedData, isPublic = false) {
    const { App } = store.getState();
    const socketId = App.websocket?.websocket?.socketId();

    const headers = {
        "Content-Type": "application/json",
        Accept: "application/json",
        "X-Socket-Id": socketId,
    };

    const data = providedData ? { ...providedData } : null;

    const context = data && data.context ? data.context : {};
    if (data && data.context)
        delete data.context;
    if (data && context.allow_intercept)
        data.allow_intercept = true;

    const { accessToken, companySubdomain } = getAccessTokenAndCompanySubdomain();

    if (accessToken && !isPublic)
        headers.Authorization = `Bearer ${accessToken}`;
    if (companySubdomain)
        headers['Company-Subdomain'] = companySubdomain;

    // Query string
    let query = '';
    if ((method === 'GET' || method === 'DELETE') && data)
        query = `?${queryString.stringify(data, { arrayFormat: 'bracket' })}`;

    // Options
    const options = {
        method,
        mode: "cors",
        headers,
    };
    if ((method !== 'GET' && method !== 'DELETE') && data)
        options.body = JSON.stringify(data);

    try {
        const apiPath = apiUrl + path + query;

        // only Log API call if in protected pages (authenticated)
        // public pages actions don't count towards activity
        if (accessToken) {
            store.dispatch(LoggerActions.logLastApiCall({
                time: dayjs().format(MomentFormatEnums.date_time),
                apiPath,
                method,
                data,
            }));
        }
        const response = await fetch(apiUrl + path + query, options);
        return handleResponse(response, context);
    }
    catch (e) {
        console.error(e);
        throw new Error(t`Unable to load the content, please check your Internet connection.`);
    }
}
export async function post(path, data) {
    if (data && data.is_public) {
        delete data.is_public;
        return request('POST', path, data, true);
    }

    return request('POST', path, data);
}

post.method = 'POST';
export async function puto(path, data) {
    return request('PUT', path, data);
}

puto.method = "PUT";
export async function patch(path, data) {
    return request('PATCH', path, data);
}

patch.method = "PATCH";
export async function get(path, data = null) {
    return request('GET', path, data);
}

get.method = "GET";
export async function deleto(path, data = null) {
    return request('DELETE', path, data);
}

deleto.method = "DELETE";

export function* interceptedCall(fn, path, data = null, action = {}) {
    const response = yield call(fn, path, { ...data, allow_intercept: true, intercept_action: action });
    yield sagaPut({ type: AppActions.CLOSE_INTERCEPTOR });
    return response;
}
